<template>
    <div style="position: relative;">
        <div v-if="overlayActive" class="interaction-overlay"></div>
        <div v-if="overlayActive && !verifyCircle" class="overlay-map-info">Click on the map to start drawing.</div>
        <div class="overlay-map-info" v-if="overlayActive && verifyCircle ">
            Satisfied with the selected area?
            <br>
            <v-btn  class="text-decoration-underline" variant="plain"
                    @click="()=>{verifyCircle = false; startCircleDraw()}"
            >No, Redraw</v-btn>
            <v-btn class="text-decoration-underline" variant="plain" 
                @click="()=>{overlayActive = false; addAreaView = true; verifyCircle = false;}"
                >
                Yes
            </v-btn>
        </div>
        <ExclusionAreaSidebar 
            :exclusionAreas="exclusionAreas"
            @sidebarEvent="onAreaSelect" 
            @addExclusionArea="openAddDialog"
            style="z-index: 10;"
        />
        <v-main>
            <div id="mapContainer"></div>
            <div id="inputContainer">
                <div v-if="!selectedArea" class="center height full-space text-body-2">
                    Select an exclusion area on the map or from the list on the left.
                </div>
                <div v-if="selectedArea" class="selected-container full-space">
                    <div class="full-space">
                        <ExclusionAreaEditForm 
                            :selectedArea="selectedArea"
                            :isEditing="isEditing"
                            :isCircleDrawing="isCircleDrawing"
                            :triggerSend="triggerEditSend"
                            :positionUpdate="editPositionUpdate"
                            @circleDrawStart="startCircleDraw"
                            @send="(area)=>{
                                selectedArea = area;
                                triggerEditSend = false;
                                onConfirmEdit();}"
                            @error="()=>{
                                triggerEditSend = false;
                                showSnackbar('Could not update exclusion area. Verify the data and try again.')
                            }"
                        />
                    </div>
                    <div class="button-area">
                        <v-btn v-if="!isEditing" @click="isEditing=true"><v-icon>mdi-pencil</v-icon>Edit</v-btn>
                        <v-btn v-if="isEditing" style="background-color: #a7d1ab;" @click="triggerEditSend = true"><v-icon>mdi-content-save-all</v-icon>Save</v-btn>
                        <v-btn v-if="isEditing" style="background-color: #f09999;" @click="onCancelEdit"><v-icon>mdi-cancel</v-icon>Cancel</v-btn>
                        <v-btn  @click="flyToSelectedArea"><v-icon>mdi-map-marker-radius</v-icon>Show on map</v-btn>
                        <v-btn @click="onEnableDisable">
                            <v-icon>{{this.selectedArea.active ? 'mdi-cancel' : 'mdi-power' }}</v-icon>
                            {{this.selectedArea.active ? 'Disable' : 'Enable' }}
                        </v-btn>
                        <v-btn @click="onDelete"><v-icon>mdi-trash-can-outline</v-icon>Delete</v-btn>
                        <v-btn v-if="!isEditing" @click="onUnselectArea"><v-icon>mdi-close</v-icon>Close</v-btn>
                    </div>
                </div>
            </div>
            <ConfirmationDialog
                :message="dialog.message"
                :show="dialog.show"
                @confirmationEvent="handleDialogConfirmation"
            />
            <v-snackbar
                v-model="snackbar.show"
                :timeout="4000"
                multi-line
                :dark="false"
                color="white"
                location="top center"
            >
                {{snackbar.message}}
            </v-snackbar>
            <ExclusionAreaFormDialog
                :show="addAreaView"
                :info="circleInfo"
                @close="()=>{
                    addAreaView = false; 
                    isCreating = false; 
                    this.circleControl?.onStopCircleDrawing()}"
                @pick-coordinates="addAreaPickCoordinates"
                @submit="addExlusionArea"
                @error="showSnackbar('Could not create exclusion area. Verify the data and try again.')"
            />
        </v-main>
    </div>
</template>
<script>

import ExclusionAreaSidebar from "@/components/ExclusionAreaSidebar.vue";
import "maplibre-gl/dist/maplibre-gl.css";
import maplibregl from 'maplibre-gl';
import authorizedFetch from "@/services/AuthorizedFetch";
import MapService from "@/services/MapService";
import ConfirmationDialog from "@/components/ConfirmationDialog.vue";
import CircleDrawControl from "@/utils/CircleDrawControl";
import ExclusionAreaFormDialog from "@/components/ExclusionAreaFormDialog.vue";
import ExclusionAreaEditForm from "@/components/ExclusionAreaEditForm.vue";
export default{
    name: 'ExclusionView',
    components:{
        ExclusionAreaSidebar,
        ConfirmationDialog,
        ExclusionAreaFormDialog,
        ExclusionAreaEditForm
    },
    data(){
        return{
            map: undefined,
            center: [8, 62],
            zoomLevel: 4,
            selectedArea: null,
            isEditing: false,
            isCreating: false,
            isCircleDrawing: false,
            circleControl: null,
            addAreaView: false,
            overlayActive: false,
            verifyCircle: false,
            triggerEditSend: false,
            exclusionAreas: {
                "type": "FeatureCollection",
                "features": []
            },
            snackbar: {
                show: false,
                message: ""
            },
            dialog: {
                show: false,
                message: "",
                type:null
            },
            circleInfo: {
                center: null,
                radius: null
            },
            editPositionUpdate: {
                radius: null,
                center: null
            }
        }
    },
    methods:{
        setupMap(){
            const cmap  = new maplibregl.Map({
                container: 'mapContainer',
                style: 'https://api.maptiler.com/maps/bright-v2/style.json?key=wVAa9X69krko1sqQfQmc',
                center: this.center,
                zoom: this.zoomLevel
            });
            const popup = new maplibregl.Popup({
                closeButton: false,
                closeOnClick: false
            });
            cmap.on('load', () => {
                cmap.addSource(MapService.getExclusionSource(), {
                    'type': 'geojson',
                    'data': this.exclusionAreas
                })
                cmap.addLayer(MapService.getExclusionStatusLayer(MapService.getExclusionSource()))

                this.circleControl = new CircleDrawControl({
                    onComplete: ({center, radius}) =>{
                        if(this.selectedArea && this.isEditing){
                            this.editPositionUpdate = {
                                center: [parseFloat(center[0].toFixed(5)), parseFloat(center[1].toFixed(5))],
                                radius: radius
                            };
                            this.showSnackbar("Center coordinates and radius updated. Click Save to apply changes.");
                        }else if (this.isCreating){
                            this.circleInfo = {
                                center: [parseFloat(center[0].toFixed(5)), parseFloat(center[1].toFixed(5))],
                                radius: radius
                            }
                            this.verifyCircle = true;
                        }
                        this.isCircleDrawing = false;
                        cmap.removeControl(this.circleControl);
                    },
                });
            });

            cmap.on('mouseenter', 'exclusion-layer',(e) => {
                cmap.getCanvas().style.cursor = 'pointer';
                const feature = e.features[0];
                const id = feature.properties.id;
                const html = `<strong>ID ${id}</strong><br> ${feature.properties.name} <br>Radius: ${feature.properties.radius} m`;
                const centerCoords = JSON.parse(feature.properties.center);
                popup.setLngLat({lng:centerCoords[0],lat:centerCoords[1]}).setHTML(html).addTo(cmap);
            });

            cmap.on('mouseleave','exclusion-layer', () => {
                cmap.getCanvas().style.cursor = '';
                popup.remove();
            });

            cmap.on('click', 'exclusion-layer', (e) => {
                if(e.features.length > 0){
                    const feature = e.features[0];
                    const id = feature.properties.id;
                    this.onAreaSelect(id);
                }
            });
            this.map = cmap;
        },
        openAddDialog(){
            this.addAreaView = true;
            this.isCreating = true;
            this.isEditing = false;
            this.selectedArea = null;
        },
        startCircleDraw(){
            if(this.circleControl && this.map){
                this.isCircleDrawing = true;
                this.circleControl.onStopCircleDrawing();
                this.map.addControl(this.circleControl, 'top-left');
            }
        },
        showSnackbar(message){
            this.snackbar.show = true;
            this.snackbar.message = message;
        },
        onDelete(){
            if(!this.selectedArea) return;
            this.dialog.message = "Are you sure you want to permanently delete this exclusion area?";
            this.dialog.type = 'confirmDelete';
            this.dialog.show = true;
        },
        onConfirmEdit(){
            this.dialog.message = "Are you sure you want to perfrom the current changes?";
            this.dialog.type = 'confirmEdit';
            this.dialog.show = true;
        },
        onEnableDisable(){
            if(!this.selectedArea) return;
            this.dialog.message = `Are you sure you want to ${this.selectedArea.active ? 'disable' : 'enable'} this area?`;
            this.dialog.type = 'enableDisable';
            this.dialog.show = true;
        },
        onAreaSelect(id){
            if(this.circleControl) this.circleControl.onStopCircleDrawing();
            this.isEditing = false;
            this.selectedArea = structuredClone(this.exclusionAreas.features
                                .find(area => area.properties.id === id).properties);
            this.flyToSelectedArea();
        },
        onCancelEdit(){
            this.isEditing = false;
            if(this.selectedArea){
                this.selectedArea = structuredClone(this.exclusionAreas.features
                                .find(area => area.properties.id === this.selectedArea.id).properties);
            }
            if (this.circleControl) this.circleControl.onStopCircleDrawing();
        },
        onUnselectArea(){
            this.selectedArea = null;
            if (this.circleControl) this.circleControl.onStopCircleDrawing();
        },
        flyToSelectedArea(){
            if(!this.selectedArea) return;
            this.map.flyTo({
                center: this.selectedArea.center,
                zoom: 11
            });
        },
        addAreaPickCoordinates(){
            if(!this.isCreating) return;
            this.overlayActive = true;
            this.addAreaView = false;
            this.startCircleDraw();
        },
        handleDialogConfirmation(action){
            this.dialog.show = false;
            if(action === 'confirm'){
                if(this.dialog.type === 'confirmEdit'){
                    this.isEditing = false;
                    if(this.selectedArea) this.updateSelectedArea();
                    if (this.circleControl) this.circleControl.onStopCircleDrawing();
                }
                else if (this.dialog.type === 'enableDisable'){
                    this.enableDisableArea();
                }
                else if (this.dialog.type === 'confirmDelete'){
                    this.deleteSelectedArea();
                }
            }
            this.dialog.type = null;
            this.dialog.message = "";
        },
        fetchExclusionAreas(){
            authorizedFetch('/api/incident/exclusion-areas')
                .then((response) => response.json())
                .then((data) => {
                    let features = [];
                    for(let feat of data){
                        try{
                            const feature = JSON.parse(feat);
                            features.push(feature);
                        }catch (e) {
                            console.error(e);
                        }
                    }
                    this.exclusionAreas = {
                        'type': 'FeatureCollection',
                        'features': features
                    }
                    if(this.map) this.map.getSource(MapService.getExclusionSource()).setData(this.exclusionAreas)
                });
        },
        async updateSelectedArea(){
            try{
                const response = await authorizedFetch('/api/incident/exclusion-area/'+this.selectedArea.id,{
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(this.selectedArea)
                })
                
                if(response.ok){
                    const data = await response.json();
                    const index = this.exclusionAreas.features.findIndex(
                        area => area.properties.id === this.selectedArea.id
                    )
                    if(index !== -1){
                        this.exclusionAreas.features.splice(index, 1, data);
                        this.map.getSource(MapService.getExclusionSource()).setData(this.exclusionAreas);
                        this.showSnackbar("Exclusion area updated successfully.");
                    }
                    if(this.selectedArea){
                        this.selectedArea = structuredClone(data.properties);
                    }
                }
            }catch (e) {
                console.error('Error updating exclusion area:', e);
                this.showSnackbar("Could not update exclusion area.");
            }
        },
        async enableDisableArea(){
            if(!this.selectedArea) return;
            try{
                let url = '/api/incident/exclusion-area/' + this.selectedArea.id + 
                      '/' + (this.selectedArea.active ? 'disable' : 'enable');
                const response = await authorizedFetch(url);
                if(response.ok){
                    const data = await response.json();
                    const index = this.exclusionAreas.features.findIndex(
                        area => area.properties.id === this.selectedArea.id
                    );
                    if(index !== -1){
                        this.exclusionAreas.features.splice(index, 1, data);
                        this.map.getSource(MapService.getExclusionSource()).setData(this.exclusionAreas);
                        this.selectedArea = structuredClone(data.properties);
                        this.showSnackbar(`Exclusion area is ${this.selectedArea.active ? 'enabled' : 'disabled'}`);
                    }
                }
            } catch (e) {
                console.error('Error enabling/disabling exclusion area:', e);
                this.showSnackbar("Could not change exclusion area status.");
            }
        },
        async addExlusionArea(area){
            this.isCreating = false;
            this.addAreaView = false;
            this.circleControl?.onStopCircleDrawing();

            try{
                const response = await authorizedFetch('/api/incident/exclusion-area', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(area)
                });

                if(response.ok){
                    const data = await response.json();
                    this.exclusionAreas.features.push(data);
                    this.map.getSource(MapService.getExclusionSource()).setData(this.exclusionAreas);
                    this.showSnackbar("Exclusion area created successfully.");
                }else{
                    throw new Error('Failed to create exclusion area');
                }
            }catch (e) {
                console.error('Error creating exclusion area:', e);
                this.showSnackbar("Could not create exclusion area. Try again.");
            }
        },
        async deleteSelectedArea(){
            if(!this.selectedArea) return;
            try{
                const response = await authorizedFetch('/api/incident/exclusion-area/' + this.selectedArea.id, {
                    method: 'DELETE'
                });
                if(response.ok){
                    const index = this.exclusionAreas.features.findIndex(
                        area => area.properties.id === this.selectedArea.id
                    );
                    if(index !== -1){
                        this.exclusionAreas.features.splice(index, 1);
                        this.map.getSource(MapService.getExclusionSource()).setData(this.exclusionAreas);
                        this.showSnackbar("Exclusion area deleted successfully.");
                        this.selectedArea = null;
                        this.isEditing = false;
                    }
                }else{
                    throw new Error('Failed to delete exclusion area');
                }
            }catch (e) {
                console.error('Error deleting exclusion area:', e);
                this.showSnackbar("Could not delete exclusion area. Try again.");
            }
        }
    },
    mounted(){
        this.setupMap();
        this.fetchExclusionAreas();
    },
    beforeUnmount() {
        if (this.map) {
            this.map.remove();
        }
    },
    watch: {
        isCircleDrawing(newValue){
            if(this.map && this.map.getLayer('exclusion-layer')){
                this.map.setLayoutProperty('exclusion-layer', 'visibility', newValue ? 'none' : 'visible');
            }
        }
    }
}

</script>
<style scoped>
#mapContainer {
    width: 100%;
    height: 60vh;
    position: relative;
    z-index: 1001;
}
#inputContainer {
    width: 100%;
    font: 12px/20px 'Helvetica Neue', Arial, Helvetica, sans-serif;
}
.height{
    height: calc(40vh - 64px) !important; /* 64px is the height of the nav bar */
}
.center{
    display: flex;
    justify-content: center;
    align-items: center;
}
.full-space{
    width: 100%;
    height: 100%;
}
.selected-container{
    display: flex;
}
.button-area{
    width: 200px;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    align-items: center;
    border-left: 1px solid #eeeeee;
}
.button-area .v-btn{
    font-size: 12px;
    width: 150px;
}
.interaction-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(235, 235, 235, 0.7);
    z-index: 1000;
}
.overlay-map-info{
    position: absolute;
    top: 70px;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(255,255,255,0.9);
    z-index: 3000;
    padding: 5px 20px;
    border-radius: 10px;
}

</style>